<template>
  <div id="navigation">
    <div class="ui inverted huge borderless fixed fluid menu">
      <router-link to="/" v-if="!isGuest"><a class="header item">Teacher Portal</a></router-link>
      <p v-else class="header item">Teacher Portal</p>
      <div class="right menu">
        <!-- <active-license /> -->
        <div v-if="user && user.profile && user.profile.role === 'Administrator'" class="right menu">
          <button class="ui button negative" v-on:click="resetSchoolSelection()">
            Reset
          </button>
          <sui-dropdown style="width: 500px" fluid :options="schools" placeholder="Select a school" search selection
            v-model="currentSchool" />
          <div class="ui checkbox" style="margin-left: 10px; margin-right: 10px; align-self: center;">
            <input type="checkbox" id="activeSchoolsOnly" v-model="activeOnly"></input>
            <label for="activeSchoolsOnly" style="color: white; cursor: pointer;">Active Only</label>
          </div>
        </div>
        <button class="ui button primary" v-on:click="profile()">
          Profile
        </button>
        <button class="ui red button" v-on:click="signOut()">Log out</button>
      </div>
    </div>
    <div class="ui grid">
      <div class="row">
        <div class="column" id="sidebar" v-if="!isGuest">
          <div class="ui secondary vertical fluid menu">
            <router-link to="/" class="item">
              <i class="blue home icon"></i>Home
            </router-link>
            <router-link to="/class/select" class="item">
              <i class="orange users icon"></i>Classes
            </router-link>
            <router-link to="/test/select" class="item">
              <i class="teal chart bar icon"></i>Reports
            </router-link>
            <router-link to="/training" class="item">
              <i class="purple book icon"></i>Training
            </router-link>
            <div class="item resources-nav-item">
              <resource-nav :node="resourcesTree" :depth="0" />
            </div>
            <router-link
              v-if="user && user.profile && user.profile.role === 'Administrator'"
              to="/test/verification"
              class="item"
            >
              <i class="grey clipboard check icon"></i>Test
            </router-link>
          </div>
        </div>
        <div class="column" id="content">
          <div class="ui basic blurring segment dimmable" :class="isLoading() ? 'dimmed' : ''">
            <div class="ui inverted dimmer transition" :class="isLoading() ? 'visible active' : 'hidden'">
              <div class="ui text loader">Loading</div>
            </div>
            <router-view />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { UserManager, WebStorageStateStore } from 'oidc-client';
import ActiveLicense from './license/ActiveLicense';
import ResourceNav from './resources/ResourceNav';
import { resourcesTree } from '../data/resources';

export default {
  components: { ActiveLicense, ResourceNav },
  name: 'Navigation',
  computed: {
    ...mapGetters({
      signedIn: 'signedIn',
      user: 'user',
      notAuthorized: 'notAuthorized',
      loading: 'loading',
      schoolSelection: 'schoolSelection',
      currentSchoolCode: 'currentSchoolCode',
    }),
    isGuest: {
      get() {
        return !this.user || (this.user && this.user.profile &&
          (this.user.profile.role === 'Guest'));
      },
    },
    isAdministrator: {
      get() {
        return !this.user || (this.user && this.user.profile &&
          (this.user.profile.role === 'Administrator'));
      },
    },
    schools: {
      get() {
        if (!this.schoolSelection) {
          return [];
        }
        // eslint-disable-next-line arrow-parens
        const schoolSelection = this.schoolSelection.map((school) => ({
          key: school.rollNumber,
          text: `${school.name} (${school.rollNumber})`,
          value: school.rollNumber,
        }));
        return schoolSelection;
      },
    },
    activeOnly: {
      get() {
        return this.$store.getters.activeOnly;
      },
      set(value) {
        this.$store.dispatch('setActiveOnly', value);
      },
    },
  },
  methods: {
    signIn() {
      // Save current location to return after authentication
      const returnUrl = window.location.href;
      // eslint-disable-next-line arrow-parens
      this.mgr.signinRedirect({ state: returnUrl }).catch((err) => console.log(err));
    },
    signOut() {
      if (this.isAdministrator) {
        this.$store
          .dispatch('updateSchoolCode', { schoolCode: 'admin' })
          .then(() => {
            this.$store.dispatch('getSchoolCode');
            // Save current location to localStorage for post-logout navigation
            // We use localStorage because the OIDC logout flow may not preserve state reliably
            const returnUrl = window.location.href;
            localStorage.setItem('post_logout_return_url', returnUrl);

            this.mgr
              .signoutRedirect()
              .then(() => {
                this.$store.dispatch('setUser', null);
              })
              // eslint-disable-next-line arrow-parens
              .catch((err) => console.log(err));
          });
      } else {
        // Save current location to localStorage for post-logout navigation
        // We use localStorage because the OIDC logout flow may not preserve state reliably
        const returnUrl = window.location.href;
        localStorage.setItem('post_logout_return_url', returnUrl);

        this.mgr
          .signoutRedirect()
          .then(() => {
            this.$store.dispatch('setUser', null);
          })
          // eslint-disable-next-line arrow-parens
          .catch((err) => console.log(err));
      }
    },
    profile() {
      window.location = process.env.OIDC_AUTHORITY;
    },
    getUser() {
      const self = this;
      this.mgr
        .getUser()
        .then((user) => {
          if (user == null || user.expired) {
            self.signIn();
          } else {
            this.$store.dispatch('setUser', user);

            // Check if there's a post-logout return URL to redirect to
            const postLogoutReturnUrl = localStorage.getItem('post_logout_return_url');
            if (postLogoutReturnUrl) {
              localStorage.removeItem('post_logout_return_url');

              // Validate it's a safe URL from our domain
              try {
                const url = new URL(postLogoutReturnUrl);
                const currentOrigin = window.location.origin;

                if (url.origin === currentOrigin && url.href !== window.location.href) {
                  // Only redirect if it's different from current location
                  window.location.href = postLogoutReturnUrl;
                }
              } catch (e) {
                console.warn('Invalid post-logout return URL:', e);
              }
            }
          }
        })
        // eslint-disable-next-line arrow-parens
        .catch((err) => console.log(err));
    },
    isLoading() {
      return this.loading;
    },
    getSchoolSelection() {
      if (
        this.user &&
        this.user.profile &&
        this.user.profile.role === 'Administrator'
      ) {
        this.$store.dispatch('getSchoolSelection', this.activeOnly);
      }
    },
    resetSchoolSelection() {
      this.$store
        .dispatch('updateSchoolCode', { schoolCode: 'admin' })
        .then(() => {
          this.$store.dispatch('getSchoolCode');
          this.signIn();
        });
    },
  },
  watch: {
    currentSchool(val) {
      const currentSchoolCode = this.user.profile.school_code;
      if (val === currentSchoolCode) {
        return;
      }
      this.$store.dispatch('updateSchoolCode', { schoolCode: val }).then(() => {
        this.$store.dispatch('getSchoolCode');
        this.signIn();
      });
    },
    activeOnly(val) {
      this.getSchoolSelection();
    },
  },

  data() {
    return {
      resourcesTree,
      currentSchool: this.$store.state.admin.currentSchoolCode || '',
      mgr: new UserManager({
        userStore: new WebStorageStateStore(),
        authority: process.env.OIDC_AUTHORITY,
        client_id: process.env.OIDC_CLIENT_ID,
        redirect_uri: process.env.OIDC_REDIRECT_URL,
        response_type: process.env.OIDC_RESPONSE_TYPE,
        scope: process.env.OIDC_SCOPE,
        post_logout_redirect_uri: process.env.OIDC_LOGOUT_REDIRECT_URL,
        loadUserInfo: true,
      }),
    };
  },
  mounted() {
    this.getUser();
    this.getSchoolSelection();
    if (
      this.user &&
      this.user.profile &&
      this.user.profile.role === 'Administrator'
    ) {
      this.$store.dispatch('getSchoolCode');
    }
  },
};
</script>

<style type="text/css">
body {
  display: relative;
}

#sidebar {
  position: fixed;
  top: 51.8px;
  right: 0;
  bottom: 0;
  width: 18%;
  background-color: #f5f5f5;
  padding: 0px;
  /* scroll the menu when it is taller than the viewport */
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

#sidebar .ui.menu {
  margin: 2em 0;
  font-size: 16px;
}

#sidebar .ui.menu a.item {
  color: #337ab7;
  border-radius: 0 !important;
  /* line the icons up with the Resources tree icons */
  padding: 0.5em 1em 0.5em 1.15em;
  line-height: 1.3;
}

/* keep the icon on the left of the label (Semantic floats vertical-menu icons right) */
#sidebar .ui.menu a.item>i.icon {
  float: none;
  width: 1.18em;
  margin: 0 0.5em 0 0;
}

#sidebar .ui.menu a.item.router-link-exact-active {
  background-color: transparent;
  color: #23527c;
  font-weight: 700;
}

#sidebar .ui.menu a.item:hover {
  background-color: #ececec;
  color: #23527c;
}

#content {
  margin-left: 20px;
  margin-right: 19%;
  width: 81%;
  margin-top: 3em;
  padding-left: 3em;
  float: left;
}

#content>.ui.grid {
  padding-right: 4em;
  padding-bottom: 3em;
}

#content h1 {
  font-size: 36px;
}

#content .ui.divider:not(.hidden) {
  margin: 0;
}

#content table.ui.table {
  border: none;
}

#content table.ui.table thead th {
  border-bottom: 2px solid #eee !important;
}

.ui.segment {
  border: 0px;
  box-shadow: none;
}

#sidebar .ui.menu > .item.resources-nav-item {
  display: block;
  padding: 0;
}

#sidebar .ui.menu > .item.resources-nav-item::before {
  display: none;
}

#sidebar .resource-nav-link {
  color: #337ab7;
}

#sidebar .resource-nav-link:hover {
  background-color: #ececec;
  color: #23527c;
}

#sidebar .resource-nav-link.router-link-exact-active {
  color: #23527c;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/components/Navigation.vue